import { useState, useEffect, useRef } from 'react';

export interface FAQItem {
  question: string;
  answer: string;
}

interface FAQSectionProps {
  faqs: FAQItem[];
  title?: string;
  subtitle?: string;
  label?: string;
}

function FAQCard({ faq, index, visible }: { faq: FAQItem; index: number; visible: boolean }) {
  const [open, setOpen] = useState(false);

  return (
    <div
      className="border-b border-[#1C1C1C]/8 overflow-hidden"
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(20px)',
        transition: `opacity 0.5s ease ${index * 60}ms, transform 0.5s ease ${index * 60}ms`,
      }}
    >
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-0 py-6 text-left cursor-pointer group"
        aria-expanded={open}
      >
        <span className="font-serif font-bold text-[#1C1C1C] text-base md:text-lg pr-8 leading-snug group-hover:text-[#ec3145] transition-colors duration-300">{faq.question}</span>
        <div className={`w-8 h-8 border flex items-center justify-center flex-shrink-0 transition-all duration-300 ${open ? 'border-[#ec3145] bg-[#ec3145] text-white rotate-45' : 'border-[#1C1C1C]/15 text-[#1C1C1C]/40 group-hover:border-[#ec3145] group-hover:text-[#ec3145]'}`}>
          <i className="ri-add-line text-sm"></i>
        </div>
      </button>
      <div
        className="overflow-hidden transition-all duration-300 ease-in-out"
        style={{ maxHeight: open ? '400px' : '0px' }}
      >
        <div className="pb-6 text-sm text-[#1C1C1C]/55 leading-relaxed">
          {faq.answer}
        </div>
      </div>
    </div>
  );
}

export default function FAQSection({ faqs, title, subtitle, label }: FAQSectionProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const schemaData = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: { '@type': 'Answer', text: faq.answer },
    })),
  };

  return (
    <section className="py-24 md:py-32 bg-white">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }} />
      <div className="max-w-7xl mx-auto px-8 md:px-14">
        <div
          ref={ref}
          className="mb-16"
          style={{
            opacity: visible ? 1 : 0,
            transform: visible ? 'translateY(0)' : 'translateY(20px)',
            transition: 'opacity 0.6s ease, transform 0.6s ease',
          }}
        >
          {label && (
            <span className="text-[10px] font-bold tracking-[0.35em] uppercase text-[#ec3145] block mb-3">{label}</span>
          )}
          {title && (
            <h2 className="font-serif text-4xl md:text-5xl font-bold text-[#1C1C1C] mb-3">{title}</h2>
          )}
          {subtitle && (
            <p className="text-[#1C1C1C]/45 text-sm max-w-xl leading-relaxed">{subtitle}</p>
          )}
        </div>

        <div className="max-w-3xl border-t border-[#1C1C1C]/8">
          {faqs.map((faq, i) => (
            <FAQCard key={i} faq={faq} index={i} visible={visible} />
          ))}
        </div>
      </div>
    </section>
  );
}
